import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell as CTableCell,
  TableHead as CTableHead,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { Route as ReportAvailableRoute } from "@/routes/(pharma)/_layout/reports/supplier-profitability/index";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { useSupplierProfitabilityData } from "../-hooks/useSupplierProfitabilityData";

// TableCell and TableHead wrappers
const TableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "text-sm font-medium py-2", className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, className)} {...props} />
);

const columns = [
  { key: "supplier_name", label: "Supplier Name" },
  { key: "total_cogs", label: "Total COGS" },
  { key: "total_revenue", label: "Total Revenue" },
  { key: "gross_profit", label: "Gross Profit" },
  { key: "gross_margin", label: "Gross Margin %" },
  { key: "units_purchased", label: "Units Purchased" },
  { key: "units_sold", label: "Units Sold" },
  { key: "sell_through", label: "Sell Through Rate" },
  { key: "stock_value", label: "Stock Value at Cost" },
];
const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);



const Component = () => {
  const searchParams = ReportAvailableRoute.useSearch();
  const { data, isLoading, error, isFetching } = useSupplierProfitabilityData();
  const navigate = useNavigate();

  const totalPages = data?.totalCount && data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => {
                    navigate({
                      to: "/invoicing",
                    });
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Reports Overview
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Supplier Profitability Report</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <hr className="my-4" />

        <div className="border rounded-lg bg-white overflow-hidden mt-4">
          {isLoading ? (
            <TableLoading columns={columns} visibleColumns={visibleColumns} />
          ) : !data?.items || data.items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-gray-500">No entries found</div>
            </div>
          ) : (
            <>
              <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Supplier Name</TableHead>
                <TableHead>Total Cost of Goods Purchased</TableHead>
                <TableHead>Total Revenue Generated</TableHead>
                <TableHead>Gross Profit</TableHead>
                <TableHead>Gross Profit Margin</TableHead>
                <TableHead>Total Units Purchased</TableHead>
                <TableHead>Total Units Sold</TableHead>
                <TableHead>Sell Through Rate</TableHead>
                <TableHead>Current Stock Value at Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>{item.supplierName}</TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.totalCostOfGoodsPurchased)}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.totalRevenueGenerated)}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.grossProfit)}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.grossMarginPercentage ? `${item.grossMarginPercentage.toFixed(2)}%` : "N/A"}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.totalUnitsPurchased.toLocaleString()}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.totalUnitsSold.toLocaleString()}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {item.sellThroughRatePercentage ? `${item.sellThroughRatePercentage.toFixed(2)}%` : "N/A"}
                  </TableCell>
                  <TableCell className={index === data.totalCount - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.currentStockValueAtCost)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
          )}
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data?.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({ search: { ...searchParams, page: 1 } });
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({ search: { ...searchParams, page: searchParams.page - 1 } });
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((page, idx) => (
                  page === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={page}
                        active={searchParams.page === page}
                        onClick={() => {
                          navigate({ search: { ...searchParams, page: typeof page === 'number' ? page : searchParams.page } });
                        }}
                      >
                        <span className="text-sm">{page}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({ search: { ...searchParams, page: searchParams.page + 1 } });
                }}
                disabled={searchParams.page >= totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({ search: { ...searchParams, page: totalPages }});
                }}
                disabled={searchParams.page >= totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;
